import { SituacaoBadge } from "@/components/Presencial";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { RefreshButton } from "@/components/RefreshButton";
import { toEmbedUrl } from "@/lib/video";
import {
  ArrowLeft, Plus, Trash2, Layers, FileText, Video, Loader2, X, Pencil, Info, Users, PlaySquare, GraduationCap,
  MoreVertical, Copy, Eye, EyeOff, ArrowUp, ArrowDown, GripVertical, Search, Play, MoveRight, Save, ListChecks, ClipboardCheck,
} from "lucide-react";
import { UploadField } from "@/components/UploadField";
import { CourseFormDialog } from "@/components/CourseFormDialog";
import { Pager } from "@/components/Pager";
import { STATE_META, fmtDate } from "@/components/EnrollmentsDialog";
import { QuestionBank } from "@/components/course/QuestionBank";
import { ExamSettings } from "@/components/course/ExamSettings";
import { CityInput } from "@/components/CityInput";
import { LibraryCover } from "@/components/LibraryCover";

import { confirmar } from "@/components/ConfirmDialog";
const errMsg = (e) => formatApiError(e.response?.data?.detail);
const EMPTY_LESSON = { title: "", video_url: "", duration: "", description: "", pdfs: [], published: true };

const SECTIONS = [
  { key: "informacoes", label: "Informações", icon: Info },
  { key: "professores", label: "Professores", icon: Users },
  { key: "modulos", label: "Módulos", icon: Layers },
  { key: "aulas", label: "Aulas", icon: PlaySquare },
  { key: "questoes", label: "Banco de questões", icon: ListChecks },
  { key: "prova", label: "Prova final", icon: ClipboardCheck },
  { key: "matriculas", label: "Matrículas", icon: GraduationCap },
];

const StatusBadge = ({ on }) => (
  <span className={`text-xs font-medium px-2 py-1 rounded border whitespace-nowrap ${on ? "text-green-700 bg-green-50 border-green-200" : "text-slate-600 bg-slate-50 border-slate-200"}`}>
    {on ? "Publicado" : "Rascunho"}
  </span>
);

export default function CourseEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hash } = useLocation();
  const [course, setCourse] = useState(null);
  const [instructors, setInstructors] = useState([]);
  const [courseDialog, setCourseDialog] = useState(false);

  const load = useCallback(() => api.get(`/courses/${id}`).then((r) => setCourse(r.data)).catch(() => navigate("/admin/cursos")), [id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); api.get("/instructors").then((r) => setInstructors(r.data)).catch(() => {}); }, [load]);

  const h = hash.replace("#", "").split("?")[0];
  // presencial não tem aulas na plataforma: sem Módulos, Aulas, Banco de questões e Prova
  const secoes = course?.modality === "presencial" ? SECTIONS.filter((s) => !["modulos", "aulas", "questoes", "prova"].includes(s.key)) : SECTIONS;
  const section = secoes.some((s) => s.key === h) ? h : (course?.modality === "presencial" ? "informacoes" : "modulos");
  const current = secoes.find((s) => s.key === section);
  const go = (key) => navigate({ hash: key }, { replace: true });

  if (!course) return <div className="grid place-items-center h-64"><Loader2 className="h-6 w-6 animate-spin text-[#232D4F]" /></div>;

  const profName = (pid) => instructors.find((i) => i.id === pid)?.name || "";

  return (
    <div className="space-y-5 animate-fade-up">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={() => navigate("/admin/cursos")} className="text-slate-400 hover:text-slate-800" title="Voltar aos cursos"><ArrowLeft className="h-5 w-5" /></button>
          <div className="min-w-0">
            <div className="text-xs text-slate-400">Editar curso</div>
            <h1 className="font-display text-xl sm:text-2xl font-bold text-slate-900 truncate">{course.title}</h1>
          </div>
          <SituacaoBadge c={{ ...course, published: course.published !== false }} />
        </div>
        <Button onClick={() => navigate(`/admin/cursos/${id}/ver`)} className="text-white" style={{ background: "#232D4F" }} data-testid="btn-view-course">
          <Play className="h-4 w-4 mr-1.5" /> Ver curso
        </Button>
      </div>

      <div className="grid lg:grid-cols-[220px_1fr] gap-6 items-start">
        <nav className="bg-white border border-slate-200 rounded-2xl p-2 lg:sticky lg:top-24 flex lg:block gap-1 overflow-x-auto" data-testid="course-sections">
          {secoes.map((s) => (
            <button key={s.key} onClick={() => go(s.key)} data-testid={`course-section-${s.key}`}
              className={`shrink-0 lg:w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left text-[15px] transition-colors ${
                s.key === section ? "text-[#1E9E2A] font-semibold bg-green-50" : "text-slate-600 hover:bg-slate-50"}`}>
              <s.icon className="h-4 w-4 shrink-0" /> <span className="whitespace-nowrap">{s.label}</span>
            </button>
          ))}
        </nav>

        <div className="min-w-0 bg-white border border-slate-200 rounded-2xl p-5 sm:p-6">
          <h2 className="font-display text-2xl font-bold text-[#232D4F] flex items-center gap-2"><current.icon className="h-5 w-5 text-[#1E9E2A]" /> {current.label}</h2>
          {section === "informacoes" && <InfoSection course={course} profName={profName} onChanged={load} />}
          {section === "professores" && <ProfessorsSection course={course} instructors={instructors} onSaved={setCourse} onEditCoordinator={() => setCourseDialog(true)} />}
          {section === "modulos" && <ModulesSection course={course} setCourse={setCourse} instructors={instructors} profName={profName} reload={load} openLessons={(mid) => navigate({ hash: `aulas?m=${mid}` }, { replace: true })} />}
          {section === "aulas" && <LessonsSection course={course} setCourse={setCourse} reload={load}
            initialModule={new URLSearchParams(hash.split("?")[1] || "").get("m") || ""}
            autoNew={new URLSearchParams(hash.split("?")[1] || "").get("nova") === "1"} />}
          {section === "questoes" && <QuestionBank course={course} />}
          {section === "prova" && <ExamSettings course={course} onGoBank={() => go("questoes")} />}
          {section === "matriculas" && <CourseEnrollments courseId={id} />}
        </div>
      </div>

      <CourseFormDialog open={courseDialog} onOpenChange={setCourseDialog} course={course} onSaved={(c) => setCourse(c)} />
    </div>
  );
}

/* ------------------------------- Informações ------------------------------- */
const MONTH_OPTS = [[1, "1 mês"], [3, "3 meses"], [6, "6 meses"], [12, "Um ano"], [24, "Dois anos"]];
const UFS_BR = ["AC", "AL", "AM", "AP", "BA", "CE", "DF", "ES", "GO", "MA", "MG", "MS", "MT", "PA", "PB", "PE", "PI", "PR", "RJ", "RN", "RO", "RR", "RS", "SC", "SE", "SP", "TO"];
const Sect = ({ title }) => (
  <div className="flex items-center gap-3 pt-4">
    <span className="h-px w-10 bg-slate-200" /><h3 className="font-display text-lg text-slate-800">{title}</h3><span className="h-px flex-1 bg-slate-200" />
  </div>
);
const Fld = ({ label, req, hint, children, className = "" }) => (
  <div className={`space-y-1.5 ${className}`}>
    <Label className="text-slate-700">{req && <span className="text-red-500 mr-1">*</span>}{label}</Label>
    {children}
    {hint && <p className="text-xs text-slate-400">{hint}</p>}
  </div>
);
const Sel = ({ value, onChange, children, disabled, testid }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} data-testid={testid}
    className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm disabled:opacity-50">{children}</select>
);

function InfoSection({ course, profName, onChanged }) {
  const navigate = useNavigate();
  const [instructors, setInstructors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [allCourses, setAllCourses] = useState([]);
  const [groups, setGroups] = useState([]);
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);

  const [certTemplates, setCertTemplates] = useState([]);
  useEffect(() => { api.get("/admin/cert-templates").then((r) => setCertTemplates(r.data.items)).catch(() => {}); }, []);
  useEffect(() => {
    api.get("/instructors").then((r) => setInstructors(r.data)).catch(() => {});
    api.get("/courses").then((r) => { setCategories([...new Set(r.data.map((c) => c.category).filter(Boolean))].sort()); setAllCourses(r.data); }).catch(() => {});
    api.get("/groups").then((r) => setGroups(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    setF({
      published: course.published !== false, free: !!course.free || !Number(course.price), title: course.title || "",
      coordinator_id: course.coordinator_id || "", category: course.category || "", starts_at: course.starts_at || "",
      duration: course.duration || "", order: course.order ? String(course.order) : "", description: course.description || "",
      price: course.price ? String(course.price) : "", promo_price: course.promo_price ? String(course.promo_price) : "",
      access_months: String(course.access_months || 0), support_enabled: !!course.support_enabled, support_months: String(course.support_months || 12),
      certificate_enabled: course.certificate_enabled !== false, cert_validity_months: String(course.cert_validity_months || 0),
      cert_min_months: String(course.cert_min_months || 0), pass_score: String(course.pass_score ?? 75), cert_template: course.cert_template || "padrao",
      banner: course.banner || "",
      modality: course.modality === "presencial" ? "presencial" : "ead", city: course.city || "", state: course.state || "", venue: course.venue || "",
      address: course.address || "", event_date: course.event_date || "", event_end: course.event_end || "", event_time: course.event_time || "",
      seats: course.seats ? String(course.seats) : "", group_id: course.group_id || "", program: course.program || "",
      bonus_courses: (course.bonus_courses || []).map((b) => ({ course_id: b.course_id, fee: b.fee ? String(b.fee) : "" })),
    });
  }, [course]);

  if (!f) return null;
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const lifetime = f.access_months === "0";
  const customAccess = !["0", ...MONTH_OPTS.map(([v]) => String(v))].includes(f.access_months);

  const save = async () => {
    if (!f.title.trim()) return toast.error("Informe o título do curso.");
    if (!f.category.trim()) return toast.error("Informe a categoria.");
    if (f.modality === "presencial" && (!f.city.trim() || !f.state || !f.event_date)) return toast.error("Curso presencial: informe cidade, estado e a data.");
    setSaving(true);
    try {
      const body = {
        title: f.title.trim(), description: f.description, category: f.category.trim(), duration: f.duration, banner: f.banner,
        published: f.published, free: f.free, price: f.free ? 0 : parseFloat(String(f.price).replace(",", ".")) || 0,
        promo_price: f.free ? 0 : parseFloat(String(f.promo_price).replace(",", ".")) || 0,
        certificate_enabled: f.certificate_enabled, order: parseInt(f.order, 10) || 0, coordinator_id: f.coordinator_id,
        starts_at: f.starts_at, access_months: parseInt(f.access_months, 10) || 0,
        support_enabled: f.support_enabled, support_months: parseInt(f.support_months, 10) || 0,
        cert_validity_months: parseInt(f.cert_validity_months, 10) || 0, cert_min_months: parseInt(f.cert_min_months, 10) || 0,
        pass_score: parseInt(f.pass_score, 10) || 0, cert_template: f.cert_template,
        modality: f.modality, city: f.city.trim(), state: f.state, venue: f.venue.trim(), address: f.address.trim(),
        event_date: f.event_date, event_end: f.event_end, event_time: f.event_time.trim(), seats: parseInt(f.seats, 10) || 0, group_id: f.group_id, program: f.program,
        bonus_courses: f.modality === "presencial" ? f.bonus_courses.map((b) => ({ course_id: b.course_id, fee: parseFloat(String(b.fee).replace(",", ".")) || 0 })) : [],
      };
      if (f.modality === "presencial") Object.assign(body, { certificate_enabled: false, access_months: 0, support_enabled: false, cert_min_months: 0 });
      await api.put(`/courses/${course.id}`, body);
      toast.success("Informações do curso salvas.");
      onChanged();
      if (course.published === false && f.published) offerInvites();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  const offerInvites = async () => {
    try {
      const p = (await api.post("/admin/invites/preview", { course_id: course.id })).data;
      if (!p.total) return;
      if (!(await confirmar(`Curso publicado! Enviar agora por e-mail e WhatsApp?\n\n• ${p.pending} aluno(s) ainda sem acesso recebem o convite com o código de 1º acesso\n• ${p.active} aluno(s) já ativos recebem o aviso de matrícula`))) return;
      const r = await api.post("/admin/invites", { course_id: course.id, channels: ["email", "whatsapp"] });
      toast.success(`${r.data.invites} convite(s) e ${r.data.enroll_notices} aviso(s) na fila. Acompanhe em Usuários → Convites e avisos.`, { duration: 8000 });
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async () => {
    if (!(await confirmar(`Excluir o curso "${course.title}"? Módulos e aulas também serão apagados.`))) return;
    await api.delete(`/courses/${course.id}`); toast.success("Curso excluído."); navigate("/admin/cursos");
  };

  return (
    <div className="mt-2 space-y-5" data-testid="course-info-form">
      <p className="text-sm text-slate-500">Defina a estrutura principal do curso.</p>

      <div className="flex flex-wrap gap-8">
        <Fld label="Status" req>
          <div className="inline-grid grid-cols-2 rounded-full overflow-hidden border border-slate-200 w-56">
            {[[false, "Rascunho"], [true, "Publicado"]].map(([v, l]) => (
              <button key={l} type="button" onClick={() => set("published", v)} data-testid={`course-status-${v ? "pub" : "draft"}`}
                className={`h-10 text-sm font-medium ${f.published === v ? "bg-[#232D4F] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>{l}</button>
            ))}
          </div>
        </Fld>
        <Fld label="Curso livre? (gratuito)"><div className="h-10 flex items-center gap-2"><Switch checked={f.free} onCheckedChange={(v) => set("free", v)} /><span className="text-sm text-slate-600">{f.free ? "Sim" : "Não"}</span></div></Fld>
        {!f.free && (
          <Fld label="Valor no cartão — valor real (R$)"><Input type="number" min="0" step="0.01" value={f.price} onChange={(e) => set("price", e.target.value)} className="w-40" data-testid="course-price" />
            <p className="text-[11px] text-slate-400 mt-1">O valor cheio do curso. É o valor cobrado no cartão e aparece riscado e apagado quando há valor no Pix.</p></Fld>
        )}
        {!f.free && (
          <Fld label="Valor no Pix — com desconto (R$) — opcional"><Input type="number" min="0" step="0.01" value={f.promo_price} onChange={(e) => set("promo_price", e.target.value)} className="w-40" placeholder="Sem desconto" data-testid="course-promo-price" />
            {parseFloat(f.promo_price) > 0 && parseFloat(f.price) > 0 && (
              <p className={`text-[11px] mt-1 ${parseFloat(f.promo_price) < parseFloat(f.price) ? "text-green-700" : "text-red-600"}`}>
                {parseFloat(f.promo_price) < parseFloat(f.price) ? `No Pix o aluno paga R$ ${parseFloat(f.promo_price).toFixed(2).replace(".", ",")} (${Math.round((1 - parseFloat(f.promo_price) / parseFloat(f.price)) * 100)}% menos que no cartão). Cada valor só vale na sua forma de pagamento.` : "O valor com desconto precisa ser menor que o valor real."}
              </p>
            )}
            {!(parseFloat(f.promo_price) > 0) && <p className="text-[11px] text-slate-400 mt-1">Deixe em branco para vender pelo mesmo valor no Pix e no cartão.</p>}</Fld>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Fld label="Título do curso" req><Input value={f.title} onChange={(e) => set("title", e.target.value)} data-testid="course-info-title" /></Fld>
        <Fld label="Coordenador">
          <Sel value={f.coordinator_id} onChange={(v) => set("coordinator_id", v)}>
            <option value="">Sem coordenador(a)</option>
            {instructors.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
          </Sel>
        </Fld>
      </div>
      <Fld label="Tipo do curso" req>
        <div className="inline-grid grid-cols-2 rounded-full overflow-hidden border border-slate-200 w-72" data-testid="course-modality">
          {[["ead", "EaD (aulas online)"], ["presencial", "Presencial"]].map(([v, l]) => (
            <button key={v} type="button" onClick={() => setF((p) => ({ ...p, modality: v, category: v === "presencial" ? "Presencial" : (p.category === "Presencial" ? "Online" : p.category) }))}
              className={`h-10 text-sm font-medium ${f.modality === v ? "bg-[#1E9E2A] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>{l}</button>
          ))}
        </div>
      </Fld>

      {f.modality === "presencial" && (
        <div className="rounded-xl border border-green-200 bg-green-50/40 p-4 space-y-4" data-testid="presencial-card">
          <div className="font-semibold text-slate-800">Turma presencial</div>
          <div className="grid md:grid-cols-[2fr_90px_1fr_1fr] gap-4">
            <Fld label="Cidade" req><CityInput value={f.city} onChange={(v) => set("city", v)} onPick={(o) => setF((p) => ({ ...p, city: o.city, state: o.state }))} placeholder="Ex.: Recife" testid="presencial-city" /></Fld>
            <Fld label="UF" req>
              <Sel value={f.state} onChange={(v) => set("state", v)} testid="presencial-state">
                <option value="">—</option>{UFS_BR.map((u) => <option key={u} value={u}>{u}</option>)}
              </Sel>
            </Fld>
            <Fld label="Data de início" req><Input type="date" value={f.event_date} onChange={(e) => set("event_date", e.target.value)} data-testid="presencial-date" /></Fld>
            <Fld label="Data de fim"><Input type="date" value={f.event_end} onChange={(e) => set("event_end", e.target.value)} /></Fld>
          </div>
          <div className="grid md:grid-cols-[1fr_2fr_150px_110px] gap-4">
            <Fld label="Local"><Input value={f.venue} onChange={(e) => set("venue", e.target.value)} placeholder="Ex.: Salão Beleza Pura" /></Fld>
            <Fld label="Endereço"><Input value={f.address} onChange={(e) => set("address", e.target.value)} placeholder="Rua, número, bairro" /></Fld>
            <Fld label="Horário"><Input value={f.event_time} onChange={(e) => set("event_time", e.target.value)} placeholder="9h às 18h" /></Fld>
            <Fld label="Vagas"><Input type="number" min="0" value={f.seats} onChange={(e) => set("seats", e.target.value)} placeholder="Sem limite" /></Fld>
          </div>
          <Fld label="Grade do curso / programação" hint="Formatação: # título do dia · - tópico · 08h30 · atividade · Rótulo: item · item. Aparece formatada na página de venda e na área da aluna.">
            <Textarea rows={6} value={f.program} onChange={(e) => set("program", e.target.value)} placeholder={"1º dia – ...\n2º dia – ..."} data-testid="presencial-program-input" />
          </Fld>
          <Fld label="Turma onde a aluna entra" hint="Vazio = a aluna entra na turma de quem vendeu (professora ou representante).">
            <Sel value={f.group_id} onChange={(v) => set("group_id", v)}>
              <option value="">Turma de quem vendeu (automático)</option>
              {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
            </Sel>
          </Fld>
          <div>
            <div className="text-sm font-medium text-slate-700 mb-1">Curso(s) bônus — a aluna ganha ao pagar</div>
            <p className="text-xs text-slate-500 mb-2">Matrícula automática como "Bônus presencial", com o período de acesso do curso bônus. A taxa é cobrada só quando quem vendeu é professora <strong>franqueada</strong> (venda da professora CEO não gera taxa).</p>
            <div className="space-y-2">
              {allCourses.filter((c) => c.id !== course.id && c.modality !== "presencial").map((c) => {
                const b = f.bonus_courses.find((x) => x.course_id === c.id);
                return (
                  <div key={c.id} className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2">
                    <label className="flex items-center gap-2 flex-1 min-w-[240px] text-sm cursor-pointer">
                      <Switch checked={!!b} onCheckedChange={(v) => set("bonus_courses", v ? [...f.bonus_courses, { course_id: c.id, fee: "" }] : f.bonus_courses.filter((x) => x.course_id !== c.id))} />
                      <span className={b ? "font-medium text-slate-800" : "text-slate-600"}>{c.title}</span>
                    </label>
                    {b && <div className="flex items-center gap-2 text-xs text-slate-500">Taxa da franqueada (R$)
                      <Input value={b.fee} onChange={(e) => set("bonus_courses", f.bonus_courses.map((x) => x.course_id === c.id ? { ...x, fee: e.target.value } : x))} placeholder="0,00" className="w-24 h-9" /></div>}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-[2fr_1.3fr_1fr_0.7fr] gap-4">
        <Fld label="Categoria" req>
          <Input list="course-categories" value={f.category} onChange={(e) => set("category", e.target.value)} placeholder="Ex.: Cursos Remoto" />
          <datalist id="course-categories">{categories.map((c) => <option key={c} value={c} />)}</datalist>
        </Fld>
        <Fld label="Início das aulas" hint="Antes dessa data, o aluno vê o curso mas as aulas ficam fechadas.">
          <Input type="datetime-local" value={f.starts_at} onChange={(e) => set("starts_at", e.target.value)} data-testid="course-info-starts" />
        </Fld>
        <Fld label="Carga horária"><Input value={f.duration} onChange={(e) => set("duration", e.target.value)} placeholder="90:00" /></Fld>
        <Fld label="Ordem" req><Input type="number" min="0" value={f.order} onChange={(e) => set("order", e.target.value)} /></Fld>
      </div>
      <Fld label="Descrição"><Textarea rows={3} value={f.description} onChange={(e) => set("description", e.target.value)} /></Fld>

      {f.modality === "presencial" ? (
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600" data-testid="presencial-no-access-cert">
          <strong>Acesso e certificado no presencial:</strong> o certificado é entregue em sala de aula e o acesso online da aluna é o do <strong>curso bônus</strong> (com o período de acesso dele). Por isso, aqui não há configuração de acesso nem de certificado.
        </div>
      ) : (<>
      <Sect title="Configuração de acesso" />
      <div className="grid md:grid-cols-[200px_1fr] gap-4 items-end max-w-2xl">
        <Fld label="Acesso vitalício">
          <div className="h-10 flex items-center gap-2">
            <Switch checked={lifetime} onCheckedChange={(v) => set("access_months", v ? "0" : "12")} data-testid="course-lifetime" />
            <span className="text-sm text-slate-600">{lifetime ? "Sim" : "Não"}</span>
          </div>
        </Fld>
        <Fld label="Período de acesso" req={!lifetime} hint="Usado no bônus de matrícula curso presencial: o acesso vence nesse prazo, a partir do cadastro.">
          <div className="flex gap-2">
            <Sel value={customAccess ? "outro" : f.access_months} onChange={(v) => set("access_months", v === "outro" ? "18" : v)} disabled={lifetime} testid="course-access-months">
              {lifetime && <option value="0">Vitalício</option>}
              {MONTH_OPTS.map(([v, l]) => <option key={v} value={String(v)}>{l}</option>)}
              <option value="outro">Outro (meses)</option>
            </Sel>
            {customAccess && !lifetime && <Input type="number" min="1" max="120" value={f.access_months} onChange={(e) => set("access_months", e.target.value)} className="w-24" />}
          </div>
        </Fld>
        <Fld label="Habilitar suporte">
          <div className="h-10 flex items-center gap-2"><Switch checked={f.support_enabled} onCheckedChange={(v) => set("support_enabled", v)} /><span className="text-sm text-slate-600">{f.support_enabled ? "Sim" : "Não"}</span></div>
        </Fld>
        <Fld label="Período de suporte" hint="Informativo por enquanto (canal de suporte ao aluno em breve).">
          <Sel value={f.support_months} onChange={(v) => set("support_months", v)} disabled={!f.support_enabled}>
            {MONTH_OPTS.map(([v, l]) => <option key={v} value={String(v)}>{l}</option>)}
          </Sel>
        </Fld>
      </div>

      <Sect title="Configuração de certificados" />
      <Fld label="Certificado automático" hint="Liberado quando o aluno conclui todas as aulas publicadas.">
        <div className="h-10 flex items-center gap-2"><Switch checked={f.certificate_enabled} onCheckedChange={(v) => set("certificate_enabled", v)} data-testid="course-cert-auto" /><span className="text-sm text-slate-600">{f.certificate_enabled ? "Sim" : "Não"}</span></div>
      </Fld>
      <div className="grid md:grid-cols-2 gap-4">
        <Fld label="Validade do certificado">
          <Sel value={f.cert_validity_months} onChange={(v) => set("cert_validity_months", v)} disabled={!f.certificate_enabled}>
            <option value="0">Validade vitalícia</option>
            {[[6, "6 meses"], [12, "1 ano"], [24, "2 anos"], [36, "3 anos"], [60, "5 anos"]].map(([v, l]) => <option key={v} value={String(v)}>{l}</option>)}
          </Sel>
        </Fld>
        <Fld label="Período mínimo para cálculo da data" hint="O certificado só pode ser emitido depois desse tempo, contado do início da matrícula.">
          <Sel value={f.cert_min_months} onChange={(v) => set("cert_min_months", v)} disabled={!f.certificate_enabled} testid="course-cert-min">
            <option value="0">Sem período mínimo</option>
            {MONTH_OPTS.map(([v, l]) => <option key={v} value={String(v)}>{l}</option>)}
          </Sel>
        </Fld>
        <Fld label="Média de aprovação" req hint="Usada quando o curso tiver prova final (em breve).">
          <Sel value={f.pass_score} onChange={(v) => set("pass_score", v)}>
            {[50, 60, 70, 75, 80, 90, 100].map((n) => <option key={n} value={String(n)}>{n}% de acerto</option>)}
          </Sel>
        </Fld>
        <Fld label="Modelo de certificado" req>
          <Sel value={certTemplates.some((t) => t.id === f.cert_template && !t.is_default) ? f.cert_template : "padrao"} onChange={(v) => set("cert_template", v)}>
            <option value="padrao">Modelo padrão (o marcado como padrão em Certificados → Modelos)</option>
            {certTemplates.filter((t) => !t.is_default).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </Sel>
        </Fld>
      </div>

      </>)}
      <Sect title="Tema" />
      <Fld label="Capa do curso" hint="Proporção 3:2 (ex.: 1536×1024).">
        <div className="flex flex-wrap items-start gap-4">
          {f.banner
            ? <img src={f.banner} alt="" className="w-64 aspect-[3/2] object-cover rounded-xl border border-slate-200" />
            : <div className="w-64 aspect-[3/2] rounded-xl grid place-items-center" style={{ background: "linear-gradient(135deg,#232D4F,#1E9E2A)" }}><Layers className="h-8 w-8 text-white/70" /></div>}
          <div className="flex flex-col gap-2">
            <UploadField testid="course-info-banner" label="Enviar capa" onUploaded={(url) => set("banner", url)} />
            {f.banner && <Button type="button" variant="ghost" className="text-red-600" onClick={() => set("banner", "")}><Trash2 className="h-4 w-4 mr-1.5" /> Remover capa</Button>}
          </div>
        </div>
        <div className="mt-3"><LibraryCover city={f.modality === "presencial" ? f.city : ""} onPick={(url) => set("banner", url)} /></div>
      </Fld>

      <div className="sticky bottom-0 -mx-5 sm:-mx-6 px-5 sm:px-6 py-3 bg-white/95 backdrop-blur border-t border-slate-200 flex items-center justify-between gap-2 flex-wrap">
        <Button variant="ghost" onClick={del} className="text-red-600"><Trash2 className="h-4 w-4 mr-1.5" /> Excluir curso</Button>
        <Button onClick={save} disabled={saving} className="text-white px-8" style={{ background: "#1E9E2A" }} data-testid="course-info-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1.5" /> Salvar informações</>}
        </Button>
      </div>
    </div>
  );
}

/* ------------------------------- Professores ------------------------------- */
function ProfessorsSection({ course, instructors, onSaved, onEditCoordinator }) {
  const [sel, setSel] = useState(course.professor_ids || []);
  const [saving, setSaving] = useState(false);
  const coord = instructors.find((i) => i.id === course.coordinator_id);
  const save = async () => {
    setSaving(true);
    try { onSaved((await api.put(`/courses/${course.id}/professors`, { ids: sel })).data); toast.success("Professores atualizados."); }
    catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  return (
    <div className="mt-5 space-y-5">
      <div className="rounded-xl border border-slate-200 p-4 flex items-center gap-3 flex-wrap">
        <div className="flex-1">
          <div className="text-xs text-slate-500">Coordenador(a) do curso</div>
          <div className="font-semibold text-slate-800">{coord?.name || "Não definido"}</div>
        </div>
        <Button size="sm" variant="outline" onClick={onEditCoordinator}><Pencil className="h-4 w-4 mr-1.5" /> Alterar</Button>
      </div>
      <div>
        <p className="text-sm text-slate-600 mb-2">Professores(as) que também atuam neste curso (podem ver o conteúdo pela área do professor):</p>
        <div className="rounded-xl border border-slate-200 divide-y divide-slate-100 max-h-80 overflow-y-auto iep-scroll">
          {instructors.length === 0 && <p className="p-4 text-sm text-slate-400">Nenhum professor cadastrado. Cadastre em Usuários → Professores.</p>}
          {instructors.map((i) => (
            <label key={i.id} className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-slate-50">
              <Checkbox checked={sel.includes(i.id)} onCheckedChange={() => setSel((p) => (p.includes(i.id) ? p.filter((x) => x !== i.id) : [...p, i.id]))} />
              <span className="flex-1 text-sm text-slate-800">{i.name}</span>
              <span className="text-xs text-slate-400">{i.status === "cadastrado" ? "Ativo" : "Acesso pendente"}</span>
            </label>
          ))}
        </div>
        <div className="flex justify-end mt-3">
          <Button onClick={save} disabled={saving} className="text-white" style={{ background: "#1E9E2A" }}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1.5" /> Salvar professores</>}
          </Button>
        </div>
      </div>
    </div>
  );
}

/* --------------------------------- Módulos --------------------------------- */
function ModulesSection({ course, setCourse, instructors, profName, reload, openLessons }) {
  const [q, setQ] = useState("");
  const [dialog, setDialog] = useState(null); // {id?, title, published, professor_id}
  const [saving, setSaving] = useState(false);
  const mods = course.modules;
  const shown = mods.filter((m) => !q || m.title.toLowerCase().includes(q.toLowerCase()));

  const save = async () => {
    if (!dialog.title.trim()) return toast.error("Informe o nome do módulo.");
    setSaving(true);
    try {
      const body = { title: dialog.title, published: dialog.published, professor_id: dialog.professor_id === "none" ? "" : dialog.professor_id };
      const r = dialog.id ? await api.put(`/courses/${course.id}/modules/${dialog.id}`, body) : await api.post(`/courses/${course.id}/modules`, body);
      setCourse(r.data); setDialog(null); toast.success(dialog.id ? "Módulo atualizado." : "Módulo criado.");
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  const act = async (fn, ok) => { try { setCourse((await fn()).data); if (ok) toast.success(ok); } catch (e) { toast.error(errMsg(e)); } };
  const move = (i, d) => {
    const ids = mods.map((m) => m.id); const j = i + d;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    act(() => api.put(`/courses/${course.id}/modules-order`, { ids }));
  };
  const toggle = (m) => act(() => api.put(`/courses/${course.id}/modules/${m.id}`, { title: m.title, professor_id: m.professor_id || "", published: m.published === false }), m.published === false ? "Módulo publicado." : "Módulo movido para rascunho.");
  const del = async (m) => { if ((await confirmar(`Excluir o módulo "${m.title}" e as ${m.lessons.length} aula(s) dele?`))) act(() => api.delete(`/courses/${course.id}/modules/${m.id}`), "Módulo excluído."); };

  return (
    <div className="mt-2">
      <p className="text-sm text-slate-500">Crie módulos e organize suas aulas por tópicos.</p>
      <div className="flex flex-wrap gap-2 items-center mt-4 mb-3">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Pesquisar por módulos..." className="pl-9 h-10" />
        </div>
        <RefreshButton onClick={reload} />
        <Button onClick={() => setDialog({ title: "", published: true, professor_id: course.coordinator_id || "none" })} className="text-white" style={{ background: "#1E9E2A" }} data-testid="btn-add-module">
          <Plus className="h-4 w-4 mr-1.5" /> Novo
        </Button>
      </div>

      <div className="overflow-x-auto iep-scroll rounded-xl border border-slate-200">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase tracking-wide text-slate-500 bg-slate-50/60">
            <th className="px-4 py-3 w-24">Ordem</th><th className="px-3 py-3">Status</th><th className="px-3 py-3">Módulo</th>
            <th className="px-3 py-3 hidden md:table-cell">Professor</th><th className="px-3 py-3 hidden sm:table-cell">Aulas</th><th className="px-4 py-3 text-right">Opções</th>
          </tr></thead>
          <tbody className="divide-y divide-slate-100">
            {shown.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">{mods.length ? "Nenhum módulo com essa busca." : "Nenhum módulo ainda. Clique em Novo."}</td></tr>}
            {shown.map((m) => {
              const i = mods.indexOf(m);
              return (
                <tr key={m.id} data-testid={`module-row-${m.id}`}>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-1">
                      <span className="w-5 text-slate-600">{i + 1}</span>
                      <button onClick={() => move(i, -1)} disabled={i === 0 || !!q} className="p-1 text-slate-400 hover:text-slate-800 disabled:opacity-30" title="Subir"><ArrowUp className="h-3.5 w-3.5" /></button>
                      <button onClick={() => move(i, 1)} disabled={i === mods.length - 1 || !!q} className="p-1 text-slate-400 hover:text-slate-800 disabled:opacity-30" title="Descer"><ArrowDown className="h-3.5 w-3.5" /></button>
                    </div>
                  </td>
                  <td className="px-3 py-2.5"><StatusBadge on={m.published !== false} /></td>
                  <td className="px-3 py-2.5"><button onClick={() => openLessons(m.id)} className="font-medium text-slate-800 hover:text-[#1E9E2A] text-left">{m.title}</button></td>
                  <td className="px-3 py-2.5 hidden md:table-cell text-slate-600">{profName(m.professor_id) || "—"}</td>
                  <td className="px-3 py-2.5 hidden sm:table-cell text-slate-600">{m.lessons.length}</td>
                  <td className="px-4 py-2.5 text-right">
                    <DropdownMenu modal={false}>
                      <DropdownMenuTrigger asChild><button className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100" data-testid={`module-options-${m.id}`}><MoreVertical className="h-4 w-4" /></button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-52">
                        <DropdownMenuItem onSelect={() => setDialog({ id: m.id, title: m.title, published: m.published !== false, professor_id: m.professor_id || "none" })} data-testid={`edit-module-${m.id}`}><Pencil className="h-4 w-4 mr-2" /> Editar</DropdownMenuItem>
                        <DropdownMenuItem onSelect={() => openLessons(m.id)}><PlaySquare className="h-4 w-4 mr-2" /> Aulas ({m.lessons.length})</DropdownMenuItem>
                        <DropdownMenuItem onSelect={() => toggle(m)}>{m.published === false ? <><Eye className="h-4 w-4 mr-2" /> Publicar</> : <><EyeOff className="h-4 w-4 mr-2" /> Mover para rascunho</>}</DropdownMenuItem>
                        <DropdownMenuItem onSelect={() => act(() => api.post(`/courses/${course.id}/modules/${m.id}/copy`), "Módulo copiado (como rascunho).")}><Copy className="h-4 w-4 mr-2" /> Copiar</DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onSelect={() => del(m)} className="text-red-600" data-testid={`delete-module-${m.id}`}><Trash2 className="h-4 w-4 mr-2" /> Excluir</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-slate-400 mt-2">Total de {mods.length} módulo(s). Módulos em rascunho não aparecem para os alunos.</p>

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display">{dialog?.id ? "Editar módulo" : "Novo módulo"}</DialogTitle></DialogHeader>
          {dialog && (
            <div className="space-y-4 py-2">
              <div className="space-y-1.5"><Label>Nome do módulo *</Label>
                <Input autoFocus value={dialog.title} onChange={(e) => setDialog((p) => ({ ...p, title: e.target.value }))} onKeyDown={(e) => e.key === "Enter" && save()} placeholder="Ex.: Tricolab - Início" data-testid="module-title-input" /></div>
              <div className="space-y-1.5"><Label>Professor(a)</Label>
                <select value={dialog.professor_id} onChange={(e) => setDialog((p) => ({ ...p, professor_id: e.target.value }))} className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm">
                  <option value="none">Sem professor</option>
                  {instructors.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
                </select></div>
              <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2">
                <Label>Publicado (visível aos alunos)</Label>
                <Switch checked={dialog.published} onCheckedChange={(v) => setDialog((p) => ({ ...p, published: v }))} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} className="text-white" style={{ background: "#1E9E2A" }} data-testid="module-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------------------------- Aulas ---------------------------------- */
function LessonsSection({ course, setCourse, reload, initialModule, autoNew = false }) {
  const [moduleFilter, setModuleFilter] = useState(initialModule);
  const [q, setQ] = useState("");
  const [moveDialog, setMoveDialog] = useState(null); // { module_id, lesson }
  useEffect(() => { setModuleFilter(initialModule); }, [initialModule]);
  // vindo de "Novo módulo": já abre o cadastro da primeira aula
  useEffect(() => {
    if (autoNew && initialModule && course.modules.some((m) => m.id === initialModule)) {
      navigate(`/admin/cursos/${course.id}/aulas/nova?m=${initialModule}`, { replace: true });
    }
  }, [autoNew, initialModule]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = useMemo(() => course.modules.flatMap((m, mi) => m.lessons.map((l, li) => ({ m, mi, l, li })))
    .filter((r) => (!moduleFilter || r.m.id === moduleFilter) && (!q || r.l.title.toLowerCase().includes(q.toLowerCase()))), [course, moduleFilter, q]);

  const act = async (fn, ok) => { try { setCourse((await fn()).data); if (ok) toast.success(ok); } catch (e) { toast.error(errMsg(e)); } };
  const base = (mid) => `/courses/${course.id}/modules/${mid}/lessons`;

  const navigate = useNavigate();
  const openNew = () => {
    const mid = moduleFilter || course.modules[0]?.id;
    if (!mid) return toast.error("Crie um módulo primeiro (seção Módulos).");
    navigate(`/admin/cursos/${course.id}/aulas/nova?m=${mid}`);
  };
  const openEdit = (m, l) => navigate(`/admin/cursos/${course.id}/aulas/${l.id}/editar`);
  const moveInModule = (m, li, d) => {
    const ids = m.lessons.map((l) => l.id); const j = li + d;
    if (j < 0 || j >= ids.length) return;
    [ids[li], ids[j]] = [ids[j], ids[li]];
    act(() => api.put(`/courses/${course.id}/modules/${m.id}/lessons-order`, { ids }));
  };
  // Arrastar e soltar: mexe só dentro do mesmo módulo; atualiza a tela na hora e salva em seguida.
  const [drag, setDrag] = useState(null);   // { id, mid }
  const [over, setOver] = useState(null);   // { id, pos: "before" | "after" }
  const posicaoNaLinha = (e) => { const r = e.currentTarget.getBoundingClientRect(); return e.clientY < r.top + r.height / 2 ? "before" : "after"; };
  const onDragOverRow = (e, m, l) => {
    if (!drag || drag.mid !== m.id) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    const pos = posicaoNaLinha(e);
    if (!over || over.id !== l.id || over.pos !== pos) setOver({ id: l.id, pos });
  };
  const onDropRow = async (e, m, l) => {
    if (!drag || drag.mid !== m.id) return;
    e.preventDefault();
    const pos = posicaoNaLinha(e);
    const ids = m.lessons.map((x) => x.id).filter((id) => id !== drag.id);
    let at = ids.indexOf(l.id);
    if (at < 0) { setDrag(null); setOver(null); return; }
    if (pos === "after") at += 1;
    ids.splice(at, 0, drag.id);
    setDrag(null); setOver(null);
    if (ids.every((id, i) => id === m.lessons[i].id)) return; // ficou no mesmo lugar
    const byId = Object.fromEntries(m.lessons.map((x) => [x.id, x]));
    setCourse({ ...course, modules: course.modules.map((mod) => mod.id === m.id ? { ...mod, lessons: ids.map((id) => byId[id]) } : mod) });
    try { setCourse((await api.put(`/courses/${course.id}/modules/${m.id}/lessons-order`, { ids })).data); toast.success("Ordem das aulas salva."); }
    catch (err) { toast.error(errMsg(err)); reload(); }
  };
  const toggle = (m, l) => act(() => api.put(`${base(m.id)}/${l.id}`, { ...EMPTY_LESSON, ...l, published: l.published === false }), l.published === false ? "Aula publicada." : "Aula movida para rascunho.");
  const del = async (m, l) => { if ((await confirmar(`Excluir a aula "${l.title}"?`))) act(() => api.delete(`${base(m.id)}/${l.id}`), "Aula excluída."); };


  return (
    <div className="mt-2">
      <p className="text-sm text-slate-500">Todas as aulas do curso. Para reordenar, arraste a aula pelo ícone ⋮⋮ e solte na posição desejada (dentro do mesmo módulo), ou use as setas.</p>
      <div className="flex flex-wrap gap-2 items-center mt-4 mb-3">
        <select value={moduleFilter} onChange={(e) => setModuleFilter(e.target.value)} className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm min-w-[200px]" data-testid="lessons-module-filter">
          <option value="">Todos os módulos</option>
          {course.modules.map((m) => <option key={m.id} value={m.id}>{m.title}</option>)}
        </select>
        <div className="relative flex-1 min-w-[180px] max-w-sm">
          <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Pesquisar aulas..." className="pl-9 h-10" />
        </div>
        <RefreshButton onClick={reload} />
        <Button onClick={openNew} className="text-white" style={{ background: "#1E9E2A" }} data-testid="btn-add-lesson"><Plus className="h-4 w-4 mr-1.5" /> Nova aula{moduleFilter ? " neste módulo" : ""}</Button>
      </div>

      <div className="overflow-x-auto iep-scroll rounded-xl border border-slate-200">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase tracking-wide text-slate-500 bg-slate-50/60">
            <th className="px-4 py-3 w-24">Ordem</th><th className="px-3 py-3">Status</th><th className="px-3 py-3">Aula</th>
            <th className="px-3 py-3 hidden md:table-cell">Módulo</th><th className="px-3 py-3 hidden sm:table-cell">Conteúdo</th><th className="px-4 py-3 text-right">Opções</th>
          </tr></thead>
          <tbody className="divide-y divide-slate-100">
            {rows.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">Nenhuma aula {moduleFilter ? "neste módulo" : "ainda"}. Clique em Nova aula.</td></tr>}
            {rows.map(({ m, mi, l, li }) => (
              <tr key={l.id} data-testid={`lesson-row-${l.id}`}
                draggable={!q}
                onDragStart={(e) => { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", l.id); setTimeout(() => setDrag({ id: l.id, mid: m.id }), 0); }}
                onDragOver={(e) => onDragOverRow(e, m, l)}
                onDrop={(e) => onDropRow(e, m, l)}
                onDragEnd={() => { setDrag(null); setOver(null); }}
                className={`${drag?.id === l.id ? "opacity-40" : ""} ${over?.id === l.id && drag?.id !== l.id ? (over.pos === "before" ? "shadow-[inset_0_3px_0_0_#1E9E2A]" : "shadow-[inset_0_-3px_0_0_#1E9E2A]") : ""}`}>
                <td className="px-4 py-2.5">
                  <div className="flex items-center gap-1">
                    {!q && <span className="cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-600" title="Arraste para mudar a posição"><GripVertical className="h-4 w-4" /></span>}
                    <span className="w-8 text-slate-600">{mi + 1}.{li + 1}</span>
                    {!q && <>
                      <button onClick={() => moveInModule(m, li, -1)} disabled={li === 0} className="p-1 text-slate-400 hover:text-slate-800 disabled:opacity-30"><ArrowUp className="h-3.5 w-3.5" /></button>
                      <button onClick={() => moveInModule(m, li, 1)} disabled={li === m.lessons.length - 1} className="p-1 text-slate-400 hover:text-slate-800 disabled:opacity-30"><ArrowDown className="h-3.5 w-3.5" /></button>
                    </>}
                  </div>
                </td>
                <td className="px-3 py-2.5"><StatusBadge on={l.published !== false && m.published !== false} /></td>
                <td className="px-3 py-2.5"><button onClick={() => openEdit(m, l)} className="font-medium text-slate-800 hover:text-[#1E9E2A] text-left">{l.title}</button>{l.duration && <div className="text-xs text-slate-400">{l.duration}</div>}</td>
                <td className="px-3 py-2.5 hidden md:table-cell text-slate-600">{m.title}</td>
                <td className="px-3 py-2.5 hidden sm:table-cell text-xs text-slate-500 whitespace-nowrap">
                  {l.content_type && l.content_type !== "video" && <span className="inline-flex items-center gap-1 mr-2 font-medium text-slate-600">{{ file: "Arquivo", embed: "Embed", text: "Texto", audio: "Áudio", live: "Ao vivo" }[l.content_type]}</span>}
                  {l.video_url && <span className="inline-flex items-center gap-1 mr-2"><Video className="h-3 w-3" /> vídeo</span>}
                  {l.pdfs?.length > 0 && <span className="inline-flex items-center gap-1"><FileText className="h-3 w-3" /> {l.pdfs.length} PDF</span>}
                  {!l.video_url && !l.pdfs?.length && (!l.content_type || l.content_type === "video") && "—"}
                </td>
                <td className="px-4 py-2.5 text-right">
                  <DropdownMenu modal={false}>
                    <DropdownMenuTrigger asChild><button className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100" data-testid={`lesson-options-${l.id}`}><MoreVertical className="h-4 w-4" /></button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-52">
                      <DropdownMenuItem onSelect={() => navigate(`/admin/cursos/${course.id}/ver?aula=${l.id}`)} data-testid={`view-lesson-${l.id}`}><Play className="h-4 w-4 mr-2" /> Ver aula</DropdownMenuItem>
                      <DropdownMenuItem onSelect={() => openEdit(m, l)} data-testid={`edit-lesson-${l.id}`}><Pencil className="h-4 w-4 mr-2" /> Editar</DropdownMenuItem>
                      <DropdownMenuItem onSelect={() => toggle(m, l)}>{l.published === false ? <><Eye className="h-4 w-4 mr-2" /> Publicar</> : <><EyeOff className="h-4 w-4 mr-2" /> Mover para rascunho</>}</DropdownMenuItem>
                      <DropdownMenuItem onSelect={() => act(() => api.post(`${base(m.id)}/${l.id}/copy`), "Aula copiada (como rascunho).")}><Copy className="h-4 w-4 mr-2" /> Copiar</DropdownMenuItem>
                      {course.modules.length > 1 && <DropdownMenuItem onSelect={() => setMoveDialog({ module_id: m.id, lesson: l, to: "" })}><MoveRight className="h-4 w-4 mr-2" /> Mover para outro módulo</DropdownMenuItem>}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onSelect={() => del(m, l)} className="text-red-600"><Trash2 className="h-4 w-4 mr-2" /> Excluir</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mover aula */}
      <Dialog open={!!moveDialog} onOpenChange={(o) => !o && setMoveDialog(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-display">Mover aula</DialogTitle>
            <DialogDescription>{moveDialog?.lesson?.title}</DialogDescription></DialogHeader>
          <select value={moveDialog?.to || ""} onChange={(e) => setMoveDialog((p) => ({ ...p, to: e.target.value }))} className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm">
            <option value="">Escolha o módulo de destino…</option>
            {course.modules.filter((m) => m.id !== moveDialog?.module_id).map((m) => <option key={m.id} value={m.id}>{m.title}</option>)}
          </select>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMoveDialog(null)}>Cancelar</Button>
            <Button disabled={!moveDialog?.to} className="text-white" style={{ background: "#1E9E2A" }}
              onClick={async () => { await act(() => api.post(`${base(moveDialog.module_id)}/${moveDialog.lesson.id}/move`, { to_module_id: moveDialog.to }), "Aula movida."); setMoveDialog(null); }}>
              Mover
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* -------------------------------- Matrículas -------------------------------- */
function CourseEnrollments({ courseId }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  useEffect(() => {
    setData(null);
    api.get("/enrollments", { params: { course_id: courseId, page, limit: 50 } }).then((r) => setData(r.data)).catch(() => setData({ total: 0, rows: [] }));
  }, [courseId, page]);
  return (
    <div className="mt-2">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <p className="text-sm text-slate-500">Alunos com acesso a este curso (diretamente ou pela turma).</p>
        <Button variant="outline" onClick={() => navigate("/admin/matriculas")}>Abrir página Matrículas</Button>
      </div>
      {data === null ? <div className="py-8 grid place-items-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
        : data.rows.length === 0 ? <p className="text-sm text-slate-400 py-6">Nenhum aluno matriculado neste curso ainda.</p> : (
          <div className="rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs uppercase tracking-wide text-slate-500 bg-slate-50/60">
                <th className="px-4 py-3">Aluno</th><th className="px-3 py-3 hidden md:table-cell">Origem</th><th className="px-3 py-3">Válido até</th><th className="px-3 py-3">Situação</th>
              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                {data.rows.map((r) => {
                  const meta = STATE_META[r.state] || STATE_META.ativa;
                  return (
                    <tr key={r.student_id}>
                      <td className="px-4 py-2.5"><button onClick={() => navigate(`/admin/alunos/${r.student_id}#matriculas`)} className="font-medium text-slate-800 hover:text-[#1E9E2A] text-left">{r.student_name}</button><div className="text-xs text-slate-400">{r.email}</div></td>
                      <td className="px-3 py-2.5 hidden md:table-cell text-xs text-slate-500">{[r.direct && "Direta", ...r.groups.map((g) => `Turma: ${g}`)].filter(Boolean).join(" · ")}</td>
                      <td className="px-3 py-2.5 text-slate-700">{fmtDate(r.expires_at) || "Sem prazo"}</td>
                      <td className="px-3 py-2.5"><span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${meta.cls}`}>{meta.label}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <Pager page={page} perPage={50} total={data.total} onChange={setPage} />
          </div>
        )}
    </div>
  );
}
